import {v7 as uuidV7} from "uuid";
import GailGptConversations from "@/services/gailgpt/conversations";
import type {ChatMessage as UIChatMessage} from "@/types/chatMessages";
import {ConversationMessage, StreamingConversationMessage} from "@/services/wingman";
import {withoutWorkflowSelection} from "@/services/wingman/workflowSelection";
import {MessageFeedbackData} from "@/types/feedback";
import {
   MessageBuildState,
   MessageProcessingContext,
   extractSpecialContents,
   processAssistantContents,
   processMessageContent,
   withGenerationFailureText,
} from "@/utils/chat/messageProcessing";

/** Type guard: does the value have a non-null `error` property? */
export function hasErrorField(value: unknown): value is {error: unknown} {
   return typeof value === "object" && value !== null && "error" in value && (value as Record<string, unknown>).error != null;
}

/** Extract an error payload from an SSE data event, if present. */
export const extractErrorPayload = (data: StreamingConversationMessage): unknown => {
   if (data.type === "token_usage" || data.type === "notification") return null;
   if (hasErrorField(data)) return data.error;
   return null;
};

/** Create a client-side assistant message (for errors, limit warnings, etc.).
 *  Marked `synthetic: true` so the UI suppresses feedback controls. */
export const createAssistantMessage = (content: string): ConversationMessage => ({
   id: uuidV7(),
   contents: [{type: "text", content}],
   role: "assistant",
   author: "assistant",
   timestamp: new Date(),
   synthetic: true,
});

/** Map of tool function names to their user-facing acknowledgment translation keys. */
const TOOL_ACK_KEYS: Record<string, string> = {
   createTroubleTicket: "toolCall.createTroubleTicket",
   changePlans: "toolCall.changePlans",
};

/** Return a translated acknowledgment string for a known tool call, or null. */
export function getSafeAcknowledgment(
   functionName: string,
   t: (key: string) => string
): string | null {
   const key = TOOL_ACK_KEYS[functionName];
   return key ? t(key) : null;
}

/** Fetch feedback from Galactus and build a lookup by message ID. Best-effort — returns empty map on failure. */
export async function fetchFeedbackMap(convId: string): Promise<Map<string, MessageFeedbackData>> {
   try {
      const galactusMessages = await GailGptConversations.getAllConversationMessages(convId, {silent: true});
      const map = new Map<string, MessageFeedbackData>();
      for (const msg of galactusMessages) {
         if (msg.feedback) {
            map.set(msg.id, msg.feedback);
         }
      }
      return map;
   } catch {
      // Expected for conversations that pre-date feedback (Galactus 404)
      return new Map();
   }
}

/** Build the API payload and optimistic UI messages from the current input + attachments. */
export async function buildSendPayload(
   textToSend: string,
   files: File[],
   t: (key: string, opts?: Record<string, unknown>) => string
) {
   const messageContent = textToSend.trim()
      ? textToSend
      : t("defaultFileMessage", {count: files.length});

   const fileBuffers = await Promise.all(
      files.map(async (file) => ({
         base64: Buffer.from(await file.arrayBuffer()).toString("base64"),
         mimeType: file.type,
         fileName: file.name,
      }))
   );

   const contents: any[] = [
      {type: "text", content: messageContent},
      ...fileBuffers.map((f) => ({type: "binary", data: f.base64, mimeType: f.mimeType, fileName: f.fileName})),
   ];

   const baseTimestamp = new Date();
   const uiMessages: ConversationMessage[] = [
      {
         id: uuidV7(),
         contents: [{type: "text", content: messageContent}],
         role: "user",
         author: "self",
         timestamp: baseTimestamp,
      },
      ...fileBuffers.map((f, index) => ({
         id: uuidV7(),
         contents: [{type: "binary" as const, data: f.base64, mimeType: f.mimeType, fileName: f.fileName}],
         role: "user" as const,
         author: "self",
         timestamp: new Date(baseTimestamp.getTime() + index + 1),
      })),
   ];

   return {contents, uiMessages};
}

/** Process an assistant ConversationMessage into UIChatMessages. */
function processAssistantMessage(
   message: ConversationMessage,
   state: MessageBuildState,
   streamingMessageId: string | null,
   ctx: MessageProcessingContext,
   messageId: string | undefined,
   feedback: MessageFeedbackData | undefined,
): UIChatMessage[] {
   const result: UIChatMessage[] = [];
   const msg = withGenerationFailureText(message, ctx.t);
   const assistantResult = processAssistantContents(msg, state, streamingMessageId);

   if (assistantResult.contentParts.length > 0) {
      // If no explicit citation content items but we have sources from function results,
      // convert sources to citations so SourceFooter and inline badges render.
      // Only do this when NOT streaming — during streaming, citation events may still arrive.
      const isCurrentlyStreaming = streamingMessageId === msg.id;
      let citations = assistantResult.citations;
      if (!isCurrentlyStreaming && (!citations || citations.length === 0) && assistantResult.sources && assistantResult.sources.length > 0) {
         citations = assistantResult.sources.map((s: any, i: number) => ({
            citationNumber: i + 1,
            sourceType: s.sourceType ?? "document",
            title: s.fileName ?? s.id,
            fileName: s.fileName,
            url: s.url,
            mediaType: s.mediaType,
            pages: s.pages,
            documentId: s.id,
         }));
      }

      result.push({
         text: assistantResult.combinedText,
         isUser: false,
         role: msg.role,
         isAnimated: false,
         sources: assistantResult.sources,
         citations,
         contentParts: assistantResult.contentParts,
         messageId,
         feedback,
         recalledFacts: msg.recalledFacts,
      });
   }

   const specialContents = extractSpecialContents(msg, ctx);
   result.push(...specialContents);
   return result;
}

/** Convert a single ConversationMessage into one or more UIChatMessages. */
export function convertSingleMessage(
   msg: ConversationMessage,
   streamingMessageId: string | null,
   ctx: MessageProcessingContext
): UIChatMessage[] {
   const state: MessageBuildState = {pendingSources: null};
   const messageId = msg.synthetic ? undefined : msg.id;
   const feedback = msg.synthetic ? undefined : msg.feedback;

   if (msg.role === "assistant") {
      return processAssistantMessage(msg, state, streamingMessageId, ctx, messageId, feedback);
   }

   const result: UIChatMessage[] = [];
   for (const part of msg.contents) {
      const content = msg.role === "user" && part.type === "text"
         ? {...part, content: withoutWorkflowSelection(part.content)}
         : part;
      const chatMessage = processMessageContent(content, msg, state, ctx);
      if (chatMessage) {
         result.push({...chatMessage, messageId});
      }
   }
   return result;
}
